import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { loanApplicationSignalRConnection } from 'app/services/signalR';
import _ from 'lodash';

import { FIELD_VALUE_BROADCAST_THROTTLE_MS, PresenceSignalRMessageType } from 'constants/presence';
import useLoanId from 'hooks/useLoanId';
import { logger } from 'utils/logger';

import { trackLocalFieldEditAction } from 'store/presence/actions';

const useFieldValueBroadcast = fieldPath => {
  const loanId = useLoanId();

  const dispatch = useDispatch();

  const normalizedPath = fieldPath?.toLowerCase();

  const throttledRef = useRef(null);

  useEffect(
    () => () => {
      if (throttledRef.current) {
        throttledRef.current.cancel();
        throttledRef.current = null;
      }
    },
    [],
  );

  const broadcastChanging = useCallback(
    value => {
      if (!loanId || !normalizedPath) {
        return;
      }

      dispatch(trackLocalFieldEditAction(normalizedPath));

      if (!throttledRef.current) {
        throttledRef.current = _.throttle(
          (currentLoanId, currentFieldPath, currentValue) => {
            loanApplicationSignalRConnection
              .invoke(
                PresenceSignalRMessageType.UPDATE_FIELD_VALUE,
                currentLoanId,
                currentFieldPath,
                currentValue,
              )
              .catch(err => {
                logger.error('Failed to broadcast field value changing', {
                  loanId: currentLoanId,
                  fieldPath: currentFieldPath,
                  err,
                });
              });
          },
          FIELD_VALUE_BROADCAST_THROTTLE_MS,
          { leading: true, trailing: true },
        );
      }

      throttledRef.current(loanId, normalizedPath, value);
    },
    [loanId, normalizedPath, dispatch],
  );

  const broadcastCommitted = useCallback(
    value => {
      if (!loanId || !normalizedPath) {
        return;
      }

      dispatch(trackLocalFieldEditAction(normalizedPath));

      if (throttledRef.current) {
        throttledRef.current.cancel();
        throttledRef.current = null;
      }

      loanApplicationSignalRConnection
        .invoke(PresenceSignalRMessageType.COMMIT_FIELD_VALUE, loanId, normalizedPath, value)
        .catch(err => {
          logger.error('Failed to commit field value', {
            loanId,
            fieldPath: normalizedPath,
            err,
          });
        });
    },
    [loanId, normalizedPath, dispatch],
  );

  return {
    broadcastChanging,
    broadcastCommitted,
  };
};

export default useFieldValueBroadcast;
